import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// The dashboard manages two products: Lonnii Web (the React app) and Lonnii Desktop (the WPF app).
// Which one is shown comes from the address, so links and bookmarks open the right side;
// the last choice is remembered so the next visit starts there.
// When Lonnii Web is retired, remove the switch and the Web menu - nothing else depends on this.

export const PLATFORMS = {
  web: { id: 'web', label: 'Lonnii Web', home: '/' },
  desktop: { id: 'desktop', label: 'Lonnii Desktop', home: '/desktop' },
};

const STORAGE_KEY = 'dashboardPlatform';

// Pages that belong to the Desktop side but live outside /desktop (kept at their old address).
const DESKTOP_PATHS = ['/registrations'];

export const platformOfPath = (pathname) =>
  pathname.startsWith('/desktop') || DESKTOP_PATHS.includes(pathname) ? 'desktop' : null;

export const rememberedPlatform = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'desktop' ? 'desktop' : 'web';
  } catch {
    return 'web';
  }
};

const remember = (platform) => {
  try {
    localStorage.setItem(STORAGE_KEY, platform);
  } catch {
    // Private mode: the switch still works, it just isn't remembered.
  }
};

export default function usePlatform() {
  const location = useLocation();
  const navigate = useNavigate();

  // Shared pages (Paramètres, Admins, Messages...) keep the side the admin was on.
  const platform = platformOfPath(location.pathname) || rememberedPlatform();

  const switchTo = useCallback((next) => {
    remember(next);
    navigate(PLATFORMS[next].home);
  }, [navigate]);

  // A Desktop page opened directly (a link, a bookmark) makes Desktop the remembered side, so the
  // shared pages that follow stay on it. Web is remembered only through the switch: the home page
  // "/" sends a Desktop admin to /desktop instead (see HomeRoute in App.js).
  const markCurrent = useCallback(() => {
    if (platformOfPath(location.pathname)) remember('desktop');
  }, [location.pathname]);

  return { platform, switchTo, markCurrent };
}
